/** PREREQ Stage 5 — Product page interactions. */
(() => {
  class PrereqProduct {
    constructor(root) {
      this.root = root;
      this.gallery = root.querySelector('[data-product-gallery]');
      this.thumbs = Array.from(root.querySelectorAll('[data-product-thumb]'));
      this.media = Array.from(root.querySelectorAll('[data-product-media]'));
      this.variantSelect = root.querySelector('[data-product-variant-select]');
      this.price = root.querySelector('[data-product-current-price]');
      this.comparePrice = root.querySelector('[data-product-compare-price]');
      this.compareWrap = root.querySelector('[data-product-compare-wrap]');
      this.priceLabel = root.querySelector('[data-price-label]');
      this.status = root.querySelector('[data-product-status]');
      this.saleBadge = root.querySelector('[data-product-sale-badge]');
      this.form = root.querySelector('.prereq-product-form');
      this.formMessage = root.querySelector('[data-product-form-message]');
      this.submit = root.querySelector('[data-product-submit]');
      this.submitLabel = root.querySelector('[data-product-submit-label]');
      this.dynamicCheckout = root.querySelector('[data-dynamic-checkout]');
      this.defaultSubmitLabel = this.submitLabel?.textContent?.trim() || 'Add to cart';
      this.isSubmitting = false;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.productThumb));
      });

      this.variantSelect?.addEventListener('change', () => this.updateVariant());
      this.form?.addEventListener('submit', (event) => this.handleAddToCart(event));
    }

    showMedia(mediaId) {
      if (!mediaId) return;
      this.media.forEach((item) => {
        const active = item.dataset.productMedia === String(mediaId);
        item.hidden = !active;
        item.classList.toggle('is-hidden', !active);
      });
      this.thumbs.forEach((thumb) => {
        const active = thumb.dataset.productThumb === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    updateVariant() {
      const option = this.variantSelect?.selectedOptions?.[0];
      if (!option) return;

      const available = option.dataset.available === 'true';
      const compare = option.dataset.comparePrice || '';
      const onSale = Boolean(compare);

      if (this.price) this.price.textContent = option.dataset.price || '';
      if (this.comparePrice) this.comparePrice.textContent = compare;
      if (this.compareWrap) this.compareWrap.hidden = !onSale;
      if (this.priceLabel) this.priceLabel.textContent = onSale ? 'Sale price' : 'Price';
      if (this.saleBadge) this.saleBadge.hidden = !onSale;

      if (this.status) {
        this.status.textContent = available ? 'In stock' : 'Sold out';
        this.status.classList.toggle('prereq-status--success', available);
        this.status.classList.toggle('prereq-status--error', !available);
      }

      if (this.submit) this.submit.disabled = !available || this.isSubmitting;
      if (this.dynamicCheckout) this.dynamicCheckout.hidden = !available;
      if (this.submitLabel && !this.isSubmitting) this.submitLabel.textContent = available ? 'Add to cart' : 'Sold out';
      this.defaultSubmitLabel = available ? 'Add to cart' : 'Sold out';

      if (option.dataset.mediaId) this.showMedia(option.dataset.mediaId);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', option.value);
      window.history.replaceState({}, '', url);
    }

    setFormMessage(message = '', isError = false) {
      if (!this.formMessage) return;
      this.formMessage.textContent = message;
      this.formMessage.hidden = !message;
      this.formMessage.classList.toggle('is-error', isError);
    }

    setSubmitting(submitting) {
      this.isSubmitting = submitting;
      const option = this.variantSelect?.selectedOptions?.[0];
      const available = option ? option.dataset.available === 'true' : this.status?.classList.contains('prereq-status--success');
      if (this.submit) this.submit.disabled = submitting || !available;
      this.submit?.setAttribute('aria-busy', submitting ? 'true' : 'false');
      if (this.submitLabel) {
        this.submitLabel.textContent = submitting ? 'Adding…' : this.defaultSubmitLabel;
      }
    }

    async handleAddToCart(event) {
      event.preventDefault();
      if (!this.form || this.isSubmitting || this.submit?.disabled) return;

      this.setFormMessage('');
      this.setSubmitting(true);

      try {
        const action = new URL(this.form.action, window.location.origin);
        action.pathname = action.pathname.replace(/\/$/, '') + '.js';

        const response = await fetch(action.toString(), {
          method: 'POST',
          body: new FormData(this.form),
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.description || payload.message || 'Unable to add this item to your cart.');
        }

        await this.refreshAndOpenCartDrawer();
        this.setFormMessage('Added to cart.');
      } catch (error) {
        console.error('[PREREQ] Add to cart failed', error);
        this.setFormMessage(error.message || 'Unable to add this item to your cart. Please try again.', true);
      } finally {
        this.setSubmitting(false);
      }
    }

    async refreshAndOpenCartDrawer() {
      const headerRoot = document.querySelector('[data-prereq-header]');
      const headerSection = headerRoot?.closest('.shopify-section');
      const sectionDomId = headerSection?.id || '';
      const sectionId = sectionDomId.replace(/^shopify-section-/, '');

      if (!headerSection || !sectionId) {
        window.location.reload();
        return;
      }

      const sectionUrl = new URL(window.location.href);
      sectionUrl.search = '';
      sectionUrl.hash = '';
      sectionUrl.searchParams.set('section_id', sectionId);

      const sectionResponse = await fetch(sectionUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!sectionResponse.ok) throw new Error('Item was added, but the cart drawer could not be refreshed.');

      const html = await sectionResponse.text();
      if (!html.trim()) throw new Error('Item was added, but the cart drawer could not be refreshed.');

      // Adding from a product page refreshes the complete header section.
      // Retire the existing controller first so global keyboard/click listeners
      // from the old header remain inert after the replacement mounts.
      headerRoot?.prereqHeaderController?.destroy?.();

      headerSection.innerHTML = html;
      headerSection.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));

      requestAnimationFrame(() => {
        headerSection.querySelector('[data-prereq-cart-open]')?.click();
      });
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-prereq-product]').forEach((root) => {
      if (root.dataset.prereqProductReady === 'true') return;
      root.dataset.prereqProductReady = 'true';
      new PrereqProduct(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
